import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Caption from '../Caption';
import DrawingEditor from '../Editor/DrawingEditor';
import EmojiEditor from '../Editor/EmojiEditor';
import ImageEditor from '../Editor/ImageEditor';
import TextEditor from '../Editor/TextEditor';
import CanvasImage from './CanvasImage';

const EDITOR_COMPONENTS = {
    crop: ImageEditor,
    text: TextEditor,
    emoji: EmojiEditor,
    draw: DrawingEditor,
};

class Image extends Component {
    constructor(props) {
        super(props);
        this.state = {
            editingFile: props.media,
            imgSrc: URL.createObjectURL(props.media),
            thumbCanvasId: `${props.uniqueId}_media_preview_canvas_thumb`,
            thumbImgDimension: {
                width: 322,
                height: 220,
            },
            // Text tool persistence across Done sessions
            textBaseImgSrc: null,
            textItems: [],
            // Emoji tool persistence across Done sessions
            emojiBaseImgSrc: null,
            emojiItems: [],
            // Draw tool persistence across Done sessions
            drawBaseImgSrc: null,
            drawStrokes: [],
        };
    }

    // For non-text/non-emoji/non-draw editors: clear persisted editor state so stale items don't carry over
    handleEditComplete = editedFile => {
        const previousUrl = this.state.imgSrc;
        const nextUrl = URL.createObjectURL(editedFile);
        URL.revokeObjectURL(previousUrl);
        if (this.state.textBaseImgSrc && this.state.textBaseImgSrc !== previousUrl) {
            URL.revokeObjectURL(this.state.textBaseImgSrc);
        }
        if (this.state.emojiBaseImgSrc && this.state.emojiBaseImgSrc !== previousUrl) {
            URL.revokeObjectURL(this.state.emojiBaseImgSrc);
        }
        if (this.state.drawBaseImgSrc && this.state.drawBaseImgSrc !== previousUrl) {
            URL.revokeObjectURL(this.state.drawBaseImgSrc);
        }
        this.setState({
            editingFile: editedFile,
            imgSrc: nextUrl,
            textBaseImgSrc: null,
            textItems: [],
            emojiBaseImgSrc: null,
            emojiItems: [],
            drawBaseImgSrc: null,
            drawStrokes: [],
        });
        this.props.updateMedia(this.props.media, {
            ...this.props.media.fileDetails,
            pendingEditedFile: editedFile,
        });
    };

    // For text editor: keep textBaseImgSrc alive so we can re-bake onto the pre-text image
    handleTextEditComplete = editedFile => {
        const previousUrl = this.state.imgSrc;
        const nextUrl = URL.createObjectURL(editedFile);
        // Don't revoke textBaseImgSrc — it's the baking base for future text sessions
        if (previousUrl !== this.state.textBaseImgSrc) {
            URL.revokeObjectURL(previousUrl);
        }
        this.setState({ editingFile: editedFile, imgSrc: nextUrl });
        this.props.updateMedia(this.props.media, {
            ...this.props.media.fileDetails,
            pendingEditedFile: editedFile,
        });
    };

    handleTextItemsChange = items => {
        this.setState({ textItems: items });
    };

    // For emoji editor: keep emojiBaseImgSrc alive so future sessions re-bake onto the pre-emoji image
    handleEmojiEditComplete = editedFile => {
        const previousUrl = this.state.imgSrc;
        const nextUrl = URL.createObjectURL(editedFile);
        if (previousUrl !== this.state.emojiBaseImgSrc) {
            URL.revokeObjectURL(previousUrl);
        }
        this.setState({ editingFile: editedFile, imgSrc: nextUrl });
        this.props.updateMedia(this.props.media, {
            ...this.props.media.fileDetails,
            pendingEditedFile: editedFile,
        });
    };

    handleEmojiItemsChange = items => {
        this.setState({ emojiItems: items });
    };

    // For draw editor: keep drawBaseImgSrc alive so future sessions re-bake onto the pre-draw image
    handleDrawEditComplete = editedFile => {
        const previousUrl = this.state.imgSrc;
        const nextUrl = URL.createObjectURL(editedFile);
        if (previousUrl !== this.state.drawBaseImgSrc) {
            URL.revokeObjectURL(previousUrl);
        }
        this.setState({ editingFile: editedFile, imgSrc: nextUrl });
        this.props.updateMedia(this.props.media, {
            ...this.props.media.fileDetails,
            pendingEditedFile: editedFile,
        });
    };

    handleDrawStrokesChange = strokes => {
        this.setState({ drawStrokes: strokes });
    };

    componentDidUpdate(prevProps) {
        if (prevProps.media !== this.props.media && this.state.editingFile !== this.props.media) {
            const previousUrl = this.state.imgSrc;
            const nextUrl = URL.createObjectURL(this.props.media);
            URL.revokeObjectURL(previousUrl);
            this.setState({ editingFile: this.props.media, imgSrc: nextUrl });
        }
        // Capture the base image the first time text tool is activated
        if (this.props.activeTool === 'text' && prevProps.activeTool !== 'text' && !this.state.textBaseImgSrc) {
            this.setState({ textBaseImgSrc: this.state.imgSrc });
        }
        // Capture the base image the first time emoji tool is activated
        if (this.props.activeTool === 'emoji' && prevProps.activeTool !== 'emoji' && !this.state.emojiBaseImgSrc) {
            this.setState({ emojiBaseImgSrc: this.state.imgSrc });
        }
        // Capture the base image the first time draw tool is activated
        if (this.props.activeTool === 'draw' && prevProps.activeTool !== 'draw' && !this.state.drawBaseImgSrc) {
            this.setState({ drawBaseImgSrc: this.state.imgSrc });
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (this.props.activeTool !== nextProps.activeTool) return true;
        if (this.state.imgSrc !== nextState.imgSrc) return true;
        if (this.state.textBaseImgSrc !== nextState.textBaseImgSrc) return true;
        if (this.state.emojiBaseImgSrc !== nextState.emojiBaseImgSrc) return true;
        if (this.state.drawBaseImgSrc !== nextState.drawBaseImgSrc) return true;
        if (this.props.caption !== nextProps.caption) return true;
        return false;
    }

    generateThumb = () => {
        const _this = this;
        const img = document.createElement('img');
        img.src = this.state.imgSrc;
        img.onload = () => {
            const canvas = document.getElementById(_this.state.thumbCanvasId);
            if (!canvas) return;
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, _this.state.thumbImgDimension.width, _this.state.thumbImgDimension.height);
            ctx.drawImage(img, 0, 0, _this.state.thumbImgDimension.width, _this.state.thumbImgDimension.height);
            const dataUrl = canvas.toDataURL();
            const thumb = dataUrl.replace(/^data:image\/\w+;base64,/, '');
            _this.props.updateMedia(_this.props.media, {
                imageUrl: thumb,
                msgType: 'image',
            });
            if (_this.props.onThumbUpdate) {
                const fileId = _this.props.media?.fileDetails?.fileId;
                if (fileId) _this.props.onThumbUpdate(fileId, dataUrl);
            }
        };
    };

    render() {
        const {
            media,
            onChangeCaption,
            tabIndex,
            uniqueId,
            chatType,
            chatId,
            selectedFiles,
            isStory,
            activeTool,
            onRegisterApply,
        } = this.props;
        const {
            thumbCanvasId,
            thumbImgDimension: { width, height } = {},
            imgSrc,
            editingFile,
            textBaseImgSrc,
            textItems,
            emojiBaseImgSrc,
            emojiItems,
            drawBaseImgSrc,
            drawStrokes,
        } = this.state;

        const isTextTool = activeTool === 'text';
        const isEmojiTool = activeTool === 'emoji';
        const isDrawTool = activeTool === 'draw';
        const editorProps = isTextTool
            ? {
                  file: editingFile,
                  imgSrc: textBaseImgSrc || imgSrc,
                  onEditComplete: this.handleTextEditComplete,
                  onRegisterApply,
                  initialTextItems: textItems,
                  onTextItemsChange: this.handleTextItemsChange,
              }
            : isEmojiTool
              ? {
                    file: editingFile,
                    imgSrc: emojiBaseImgSrc || imgSrc,
                    onEditComplete: this.handleEmojiEditComplete,
                    onRegisterApply,
                    initialEmojiItems: emojiItems,
                    onEmojiItemsChange: this.handleEmojiItemsChange,
                }
              : isDrawTool
                ? {
                      file: editingFile,
                      imgSrc: drawBaseImgSrc || imgSrc,
                      onEditComplete: this.handleDrawEditComplete,
                      onRegisterApply,
                      initialStrokes: drawStrokes,
                      onStrokesChange: this.handleDrawStrokesChange,
                  }
                : {
                      file: editingFile,
                      imgSrc,
                      onEditComplete: this.handleEditComplete,
                      onRegisterApply,
                  };

        const ActiveEditor = EDITOR_COMPONENTS[activeTool];

        if (isStory && ActiveEditor) {
            return <ActiveEditor {...editorProps} />;
        }

        return (
            <div className="type-image" id={`${uniqueId}_preview`}>
                <canvas id={thumbCanvasId} width={width} height={height} style={{ display: 'none' }} />
                <CanvasImage uniqueId={uniqueId} imgSrc={imgSrc} generateThumb={this.generateThumb} />
                <Caption
                    onChangeCaption={onChangeCaption}
                    media={media}
                    tabIndex={tabIndex}
                    uniqueId={uniqueId}
                    chatType={chatType}
                    onClickSend={this.props.onClickSend}
                    chatId={chatId}
                    selectedFiles={selectedFiles}
                    isStory={isStory}
                />
            </div>
        );
    }
}

const mapStateToProps = state => ({
    mediaImageThumbData: state.mediaImageThumbData,
});

export default connect(mapStateToProps, {})(Image);

Image.propTypes = {
    media: PropTypes.object,
    updateMedia: PropTypes.func,
    onChangeCaption: PropTypes.func,
    uniqueId: PropTypes.string,
    selectedFiles: PropTypes.array,
    onClickSend: PropTypes.func,
    chatType: PropTypes.string,
    chatId: PropTypes.string,
    type: PropTypes.string,
    mediaImageThumbData: PropTypes.object,
    isStory: PropTypes.bool,
    activeTool: PropTypes.string,
    onRegisterApply: PropTypes.func,
    onThumbUpdate: PropTypes.func,
};
